import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { match } from '@/utils/pattern-match-util'
import { ErrorBlock } from '@/components/ErrorBlock'
import { byDesign } from '@/infra/design'
import imgStripeConnectingUrl from '@/assets/misc/img_stripe-connecting.svg'
import StripeConnectingIllustration from '@/components/SpotIllustration/StripeConnectingIllustration'
import imgStripeErrorUrl from '@/assets/misc/img_stripe-error.svg'
import StripeErrorIllustration from '@/components/SpotIllustration/StripeErrorIllustration'
import imgStripeSuccessUrl from '@/assets/misc/img_stripe-success.svg'
import StripeSuccessIllustration from '@/components/SpotIllustration/StripeSuccessIllustration'

type StripeConnectPayoutModalState = 'pending' | 'error' | 'done'

interface StripeConnectPayoutModalProps {
  state: StripeConnectPayoutModalState
  onCloseRequest: () => void
  showing: boolean
  error?: unknown
  corporateMode: boolean
}

export function StripeConnectPayoutModal({
  state,
  showing,
  onCloseRequest,
  error,
  corporateMode,
}: StripeConnectPayoutModalProps) {
  return (
    <ModalSheet showing={showing}>
      {match(state, {
        pending: () => (
          <ModalSheetContent>
            <ModalSheetContentBlock>
              {!corporateMode && (
                <ModalSheetContentIllustration
                  src={imgStripeConnectingUrl}
                  illustration={byDesign(
                    undefined,
                    StripeConnectingIllustration,
                  )}
                />
              )}
              <ModalSheetContentTitle>
                Connecting with Stripe
              </ModalSheetContentTitle>
              <ModalSheetContentDescription>
                We're finishing up connecting your account. Please wait while we
                finish that up for you.
              </ModalSheetContentDescription>
            </ModalSheetContentBlock>
          </ModalSheetContent>
        ),
        error: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                {!corporateMode && (
                  <ModalSheetContentIllustration
                    src={imgStripeErrorUrl}
                    illustration={byDesign(undefined, StripeErrorIllustration)}
                  />
                )}
                <ModalSheetContentTitle>
                  Oops, something went wrong!
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  We were not able to properly setup your Stripe account. Please
                  try again or visit our Help Center to find out what could have
                  happened.
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ErrorBlock tackOn="bottom" error={error} />
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button label="Close" onPress={onCloseRequest} />
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        done: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                {!corporateMode && (
                  <ModalSheetContentIllustration
                    src={imgStripeSuccessUrl}
                    illustration={byDesign(
                      undefined,
                      StripeSuccessIllustration,
                    )}
                  />
                )}
                <ModalSheetContentTitle>
                  Stripe was successfully connected!
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  You can now use it as your current payout method
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button label="Close" onPress={onCloseRequest} />
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
      })}
    </ModalSheet>
  )
}
